import {
    createContext,
    useContext,
    useState,
    useEffect,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSmallFleets } from '../../../api/small-fleets';

export type SearchParamValue = string | string[] | boolean | number | null | undefined | Date;

interface SmallFleetsContextProps {
    smallFleets: any;
    pagination: { limit: number; page: number; };
    total: number;
    loading: boolean;
    errorMessage: string | null;
    fetchData: () => void;
    setPagination: (paginator: { limit: number; page: number; }) => void;
    handleSearchParam: (key: string, value: SearchParamValue) => void;
}

const SmallFleetsContext = createContext<SmallFleetsContextProps | undefined>(undefined);

const SmallFleetsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const PAGE = 1;
    const LIMIT = 10;

    const [smallFleets, setSmallFleets] = useState<any[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [pagination, setPagination] = useState<{ limit: number; page: number; }>({
        limit: LIMIT,
        page: PAGE,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const getParams = () => {
        const params: any = {
            limit: pagination.limit,
            page: pagination.page,
            ...Object.fromEntries(searchParams.entries()),
        };

        if (params.search) {
            params.page = 1;
        }

        return params;
    };

    // const getConnectedDrivers = (drivers: any) => drivers.reduce((acc: number, driver: any) => acc + (driver.onDutyFlag ? 1 : 0), 0);

    // const getBusyDrivers = (drivers: any) => drivers.reduce((acc: number, driver: any) => acc + ((driver.isBusy && driver.onDutyFlag) ? 1 : 0), 0);

    // const mapDriversStatus = (smallFleets: any) => {
    //     const newSmallFleets = smallFleets.map((smallFleet: any) => ({
    //         ...smallFleet,
    //         driverStatus: {
    //             connected: getConnectedDrivers(smallFleet.drivers),
    //             busy: getBusyDrivers(smallFleet.drivers),
    //         },
    //     }));

    //     return newSmallFleets;
    // };

    const fetchData = () => {
        setLoading(true);

        const params = getParams();

        getSmallFleets(params)
            .then(({ data }: any) => {
                setTotal(data.meta.itemCount);
                setSmallFleets(data.data);
            })
            .catch((error) => {
                setErrorMessage(error.message);
            })
            .finally(() => setLoading(false));
    };

    const handleSearchParam = (key: string, value: SearchParamValue) => {
        if (value === null || value === undefined || (Array.isArray(value) && value.length === 0) || value === '') {
            searchParams.delete(key);
        } else if (Array.isArray(value)) {
            searchParams.set(key, JSON.stringify(value));
        } else {
            searchParams.set(key, value.toString());
        }

        setSearchParams(searchParams);
    };

    useEffect(() => {
        fetchData();
    }, [searchParams, pagination.page]);

    return (
        <SmallFleetsContext.Provider
            value={{
                smallFleets,
                pagination,
                total,
                loading,
                errorMessage,
                fetchData,
                setPagination,
                handleSearchParam,
            }}
        >
            {children}
        </SmallFleetsContext.Provider>
    );
};

const useSmallFleets = () => {
    const context = useContext(SmallFleetsContext);
    if (context === undefined) {
        throw new Error('useSmallFleets must be used within a SmallFleetsProvider');
    }
    return context;
};

export { SmallFleetsProvider, useSmallFleets };
